import { Fragment, useCallback, useEffect, useState } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesCertificateAnpr } from '..';
import { useFormik } from 'formik';
import { CertificateAnprClient, FiltersFormCertificatesAnpr } from './types';
import { transformDataset } from './utils';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { FiltersCertificatesAnpr } from 'system/components/certificates-anpr/list';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonCertificatesAnprCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import { authsObj, headCells, validationSchemaFilter } from './constants';
import Table from 'shell/components/shared/table';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { CertificatesAnpr } from 'shell/models/certificates-anpr';
import { DateTime } from 'luxon';
import { Typography } from '@mui/material';

const CertificateAnprListPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<CertificateAnprClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<CertificateAnprClient>>({
    element: null,
    open: false,
  });

  const deleteCertificate = useHttpClient(`${__MANAGEMENT_API_URL__}/postazione-anpr`, {
    options: {
      method: 'DELETE',
    },
  });

  const filterTable = useFilterTable<FiltersFormCertificatesAnpr>();

  const [certificatesAnpr, getCertificatesAnpr] = useHttpFetch<
    HttpResponseList<CertificatesAnpr>,
    Array<CertificateAnprClient>
  >(`${__MANAGEMENT_API_URL__}/postazione-anpr`, {
    onBootstrap: false,
    transformer: (response) => {
      return response.data.payload?.list?.map((item) => ({
        code: item.key.codice,
        codeAnpr: item.codicePostazioneAnpr,
        alias: item.aliasCertificato,
        endValidityDate: item.dataFineValidita
          ? DateTime.fromISO(item.dataFineValidita).toFormat('dd/MM/yyyy')
          : '',
        startValidityDate: item.dataInizioValidita
          ? DateTime.fromISO(item.dataInizioValidita).toFormat('dd/MM/yyyy')
          : '',
        lastOperator: item.codiceOperatoreUltimoAggiornamento,
        operatorCode: item.codiceOperatoreAnpr,
        operatorName: item.nomeOperatoreAnpr,
      }));
    },
  });

  const certificates = (certificatesAnpr.data as Array<CertificateAnprClient>) || [];

  const onSubmit = useCallback(
    (formValues: FiltersFormCertificatesAnpr) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterCertificatesAnpr.setFieldValue('page', 0);
      getCertificatesAnpr({ params: {}, paramsSerializer: () => queryString });
    },
    [getCertificatesAnpr]
  );

  const filterCertificatesAnpr = useFormik({
    initialValues: initialValuesCertificateAnpr,
    validationSchema: validationSchemaFilter,
    onSubmit,
  });

  useEffect(() => {
    if (!filterTable.values?.hasVisitedChild) {
      return;
    }
    filterCertificatesAnpr.setValues(filterTable.values || initialValuesCertificateAnpr);
    const queryString = transformDataset(filterTable.values || initialValuesCertificateAnpr);
    getCertificatesAnpr({ params: {}, paramsSerializer: () => queryString });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterCertificatesAnpr.values);
  }, [filterCertificatesAnpr.values]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<CertificateAnprClient> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true });
        navigate(generatePath('/sistema/certificati-postazione/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate, filterTable]
  );

  const onEditPress = useCallback(
    (cell): Promise<CertificateAnprClient> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true });
        navigate(generatePath('/sistema/certificati-postazione/modifica/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate, filterTable]
  );

  const onTrashPress = useCallback(
    (cell): Promise<CertificateAnprClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<CertificateAnprClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterCertificatesAnpr.setFieldValue('page', newPage);
    },
    [filterCertificatesAnpr.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterCertificatesAnpr.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      filterCertificatesAnpr.setValues(newDataset);
    },
    [filterCertificatesAnpr.setValues, filterCertificatesAnpr.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterCertificatesAnpr.values, orders };
      const queryString = transformDataset(newDataset);
      return (
        getCertificatesAnpr({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<CertificatesAnpr>, Array<CertificateAnprClient>>
      ).then(() => {
        filterCertificatesAnpr.setFieldValue('orders', orders);
      });
    },
    [filterCertificatesAnpr.setFieldValue, getCertificatesAnpr, filterCertificatesAnpr.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonCertificatesAnprCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo certificato postazione anpr"
        addBtnLabel="Aggiungi nuovo certificato postazione anpr"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/certificati-postazione/nuovo');
        }}
        title="Gestione Certificati Postazione Anpr"
      />
      <div className="pmi-my-3">
        <FiltersCertificatesAnpr filtersCertificateForm={filterCertificatesAnpr} />
      </div>
      {certificatesAnpr.data ? (
        certificates?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterCertificatesAnpr.values.orders || {}}
            activeActions={auths}
            page={filterCertificatesAnpr.values.page || 0}
            rowsPerPage={filterCertificatesAnpr.values.rowsPerPage}
            body={certificates}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={certificates.length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Certificati postazione anpr"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Certificato di postazione anpr"
        message={`Confermare la cancellazione del certificato della postazione anpr ${
          deleteAlertModal.element?.alias || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteCertificate({
              url: `/${deleteAlertModal.element?.code}`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Certificato di postazione anpr"
        message={`La cancellazione della postazione anpr ${deleteSuccessModal.element?.alias} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterCertificatesAnpr.values, page: 0 });
          (
            getCertificatesAnpr({
              params: {},
              paramsSerializer: () => queryString,
            }) as ResponsePromise<HttpResponseList<CertificatesAnpr>, Array<CertificateAnprClient>>
          ).then(() => setDeleteSuccessModal({ open: false, element: null }));
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterCertificatesAnpr.values, page: 0 });
          (
            getCertificatesAnpr({
              params: {},
              paramsSerializer: () => queryString,
            }) as ResponsePromise<HttpResponseList<CertificatesAnpr>, Array<CertificateAnprClient>>
          ).then(() => setDeleteSuccessModal({ open: false, element: null }));
        }}
      />
    </Fragment>
  );
};

CertificateAnprListPage.displayName = 'Certificate Anpr list page component';

export default CertificateAnprListPage;
